import axios from 'axios';
import { takeEvery, put, select, call } from 'redux-saga/effects';
import { jwtDecode } from 'jwt-decode';
import auth0 from 'auth0-js';
import actions from '../actions';
import * as selectors from '../selectors';
import * as Manipulations from '../helper/DataManipulationTasks';
import { checkLinkedAccounts, getAuthToken } from '../api/account';
import { setRequestValidTill } from '../utils';
import { getAcqAuthToken } from '../api/auth';

function getParameterByName(name, hash) {
  let match = RegExp('[#&]' + name + '=([^&]*)').exec(hash);
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
}


function getRemainingTime(requestValidTill) {
  if (!requestValidTill) {
    return { remaining: "00:00", human: "expired", expiryLocal: "" };
  }

  const expiryUtc = new Date(requestValidTill);
  if (isNaN(expiryUtc)) {
    return { remaining: "00:00", human: "invalid date", expiryLocal: "" };
  }

  // Difference in ms — always UTC, no timezone headaches
  const diffMs = Math.max(0, expiryUtc.getTime() - Date.now());
  console.log('Teh time Diff is: ', diffMs, 'The expiry is: ', expiryUtc.getTime(), ' DateNow is: ', Date.now());
  // Human-friendly "expiryLocal" just reuse expiryUtc.toString()
  // (or toISOString() if you want unambiguous UTC)
  const expiryLocal = expiryUtc.toISOString();

  let human = "expired";
  let remaining = "00:00";

  if (diffMs > 0) {
    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    remaining = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

    const minuteText = `${minutes} minute${minutes === 1 ? "" : "s"}`;
    const secondText = `${seconds} second${seconds === 1 ? "" : "s"}`;
    human = minutes && seconds
      ? `${minuteText} ${secondText}`
      : minutes
      ? minuteText
      : secondText;
  }

  return { remaining, human, expiryLocal };
}


export function* authFlow(history) {
  yield takeEvery(actions.app.temporary.sendLoginLink, function* takeSendEmailLink({
    payload: { email },
  }) {
    try {
      const apiConfig = yield select(selectors.getApiConfig);
      yield put(actions.app.appState.startLoading('Checking login link status'));
      const accountId = yield select(selectors.getAccountId);
      const acqToken = yield call(getAcqAuthToken);
      const authToken = yield call(getAuthToken, apiConfig.oAuthConfig.domain);
      // First check if email exists
      const checkEmailExistsUrl = `${apiConfig.apiUrls.client}/Email?email=${email}&accountHashId=${accountId}`;
      const emailExists = yield call(axios, {
        method: 'get',  headers: {
          Authorization: `Bearer ${acqToken.data.access_token}`,
          'Content-Type': 'application/json',
        },
        url: checkEmailExistsUrl });

      if (emailExists.data) {
        // Check login link status before sending
        yield put(actions.app.temporary.checkLoginLinkStatus(email));
        try {
          const loginLinkStatusResponse = yield call(axios, {
            method: 'post', 
            url: apiConfig.apiUrls.checkLoginRequest,
            headers: {
              Authorization: `Bearer ${authToken.data.access_token}`,
              'Content-Type': 'application/json'
            },
            data: {
              email: email,
              HashId: accountId
            }
          });
          const status = loginLinkStatusResponse.data.status;         
          yield put(actions.app.temporary.checkLoginLinkStatusSuccess(status));
          
          if (status === false) {

            const requestValidTill = loginLinkStatusResponse.data.requestValidTill;
            console.log('Returning requestValidTill:', requestValidTill);
            // Persist the expiry provided by backend (+10 minutes already applied on server)
            if (requestValidTill) {
              try {
                setRequestValidTill(requestValidTill);
              } catch (_) {}
            }

            // Link already sent, don't send again
            yield put(actions.app.appState.stopLoading());

            const { human } = getRemainingTime(requestValidTill);
            console.log('This is after getRemainingTime and human time is:', human, requestValidTill);
            yield put(actions.app.temporary.sendLoginLinkFailure({
              error: {
                errorType: 'link_already_sent',
                userFriendlyMessage: requestValidTill ? `A login link has been sent to your email. Each link can only be used once. If you've already used it, please wait ${human} before requesting a new one.`
                                                      : `A login link has been sent to your email. Each link can only be used once. If you've already used it, please wait few minutes before requesting a new one.`,
                timestamp: new Date().toISOString(),
                requestValidTill: requestValidTill,
                email: email
              }
            }));
            return;
          }
          
          // Status is false, proceed to send the link
          yield put(actions.app.appState.startLoading('Sending email'));
          const auth = new auth0.WebAuth({
            clientID: apiConfig.clientDataKeys.client_id,
            domain: apiConfig.oAuthConfig.domain,
            redirectUri: apiConfig.callback,
            scope: apiConfig.oAuthConfig.scope,
            responseType: 'token id_token',
          });
        
          auth.passwordlessStart(
            {
              connection: 'email',
              send: 'link',
              email: email,
            },
            (err) => {
              if (err) {
                let errorMessage = 'Something went wrong. Please try again later.';
        
                if (err.description) {
                  errorMessage = err.description;
                } else if (err.message) {
                  errorMessage = err.message;
                } else if (typeof err === 'string') {
                  errorMessage = err;
                }
        
                // Display the error using toast/snackbar/modal or console
                console.error('Passwordless error:', errorMessage);
                alert(errorMessage); // Replace with your toast/snackbar if needed
                return;
              }
        
              // If no error, you may want to show success feedback
              console.log('Magic link sent successfully.');
            }
          );

          yield put(actions.app.temporary.linkSentToEmail(true));
          
        } catch (statusError) {
          console.error('Error checking login link status:', statusError);
          yield put(actions.app.temporary.checkLoginLinkStatusFailure({
            error: {
              errorType: 'status_check_failed',
              userFriendlyMessage: 'Unable to check login link status. Please try again.',
              message: 'Login link status check failed',
              timestamp: new Date().toISOString(),
              email: email
            }
          }));
        }
      }
      else{
        console.log('email not found');
        yield put(actions.app.temporary.sendLoginLinkFailure({
          error: {
            errorType: 'email_not_found',
            userFriendlyMessage: 'If the email address is valid, you will receive an email with further instructions.',
            message: 'Email sent',
            timestamp: new Date().toISOString(),
            email: email
          }
        }));
      }
      yield put(actions.app.appState.stopLoading());
     
    } catch (e) {
      yield put(actions.app.appState.stopLoading());
      yield put(actions.app.temporary.sendLoginLinkFailure(e));
    }
  });

  yield takeEvery(actions.app.auth.logIntoClient, function* takeLogIntoClient({
    payload: { hash },
  }) {
    try {
      yield put(actions.app.appState.startLoading('Getting your information'));
      const idToken = getParameterByName('id_token', hash);
      const decoded = yield call(jwtDecode, idToken);
      const token = yield call(getAcqAuthToken);
      console.log('Decoded ID Token:', decoded);
      const user_id = decoded.sub;
      const apiUrls = yield select(selectors.getApiUrls);
 
      const respond = yield call(axios, {
        method: 'get',
        url: `${apiUrls.client}/Acquire?userId=${user_id}`,
        headers: {
              Authorization: `Bearer ${token.data.access_token}`,
              'Content-Type': 'application/json'
            }
      });
 
      const accountId = respond.data.accountHashId;
      const clientHashId = respond.data.hashId;
      yield put(actions.app.appState.setAccountId(accountId));
      const details = respond.data;
 
      if (respond.data) {
        // TODO: Revisit some of the Sagas as there seems to be some overlap between what they yield
        // This is only run on load so we're populating our required fields so that the component kows about it before mounting
 
        yield put(
          actions.app.appState.updateTempRequiredFields('dateOfBirth', respond.data.dateOfBirth),
        );
        yield put(actions.app.appState.updateTempRequiredFields('gender', respond.data.gender));
        yield put(
          actions.app.appState.updateTempRequiredFields(
            'maritalStatus',
            respond.data.maritalStatus,
          ),
        );
 
        yield put(
          actions.app.auth.logIntoClientSuccess(Manipulations.partnerUpdateIncoming(respond.data)),
        );
        // yield put(push('/details'));
        yield call([history, history.push], '/details');
      } else {
        yield put(
          actions.app.auth.logIntoClientSuccess({
            ...details,
            hashId: clientHashId,
          }),
        );
        // yield put(push('/details'));
        yield call([history, history.push], '/details');
      }
      yield put(actions.app.appState.stopLoading());
    } catch (e) {
      console.error('Error logging into client:', e);
      yield put(actions.app.auth.logIntoClientFailure(e));
      yield put(actions.app.appState.stopLoading());
    }
  });
 

  yield takeEvery(actions.app.auth.logout, function* takeLogOut() {
    try {
      // Do cleanups
      yield put(actions.app.auth.logoutSuccess());
      yield call([history, history.push], '/');
      // Clear any stored expiry on logout
      try { setRequestValidTill(null); } catch (_) {}
    } catch (e) {
      yield put(actions.app.auth.logoutFailure(e));
    }
  });

  yield takeEvery(actions.app.auth.checkLinkedAccounts, function* takeCheckLinkedAccounts() {
    try {
      const accessToken = yield select(selectors.getAccessToken);
      const userEmail = yield select(selectors.getUserEmail);
      const country = yield select(selectors.getCountry);

      console.log('Checking linked accounts with:', { 
        accessToken: !!accessToken, 
        userEmail, 
        country 
      });

      if (!accessToken) {
        console.warn('Access token not available for linked accounts check');
        return;
      }

      if (!userEmail) {
        console.warn('User email not available for linked accounts check');
        return;
      }
      const apiConfig = yield select(selectors.getApiConfig);
      const response = yield call(checkLinkedAccounts,apiConfig.oAuthConfig.domain, userEmail,country);
      console.log('Linked accounts API response:', response.data);
      yield put(actions.app.auth.checkLinkedAccountsSuccess(response.data));
    } catch (e) {
      console.error('Error checking linked accounts:', e);
      yield put(actions.app.auth.checkLinkedAccountsFailure(e));
    }
  });
}
